import { useState, useRef, useEffect } from "react";
import { AlignJustify, X, ChevronDown, Building2, LogOut } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MobileNavLink } from "../NavLinks";
import LogoutLink from "../LogoutLink";
import NavFooter from "../NavFooter";
import { useAdminNav } from "@/navigation/admin/NavContext";
import { ReduxState } from "@/redux/type";
import { clearProgram } from "@/redux/actions/programAction";
import { logout } from "@/redux/actions/sessionAction";
import { config } from "@/config";

const MobileNavbar = ({ navItems }: { navItems: any }) => {
    const { isOpen, toggle, close } = useAdminNav();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const session = useSelector((state: ReduxState) => state.session);
    const program = useSelector((state: ReduxState) => state.program);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isDropdownOpen]);

    const handleSwitchProgram = () => {
        setIsDropdownOpen(false);
        dispatch(clearProgram());
        navigate("/select-program");
    };

    const handleLogout = () => {
        dispatch(logout());
        dispatch(clearProgram());
        localStorage.removeItem(config.localStorageKey);
        sessionStorage.clear();
        navigate("/login", { replace: true });
    };

    const getUserInitials = () => {
        if (session.email) return session.email.substring(0, 2).toUpperCase();
        return "U";
    };

    return (
        <>
            <header className="lg:hidden w-full border-b border-gray-200 bg-white shadow-sm">
                <div className="container mx-auto flex items-center justify-between relative px-4 py-3">
                    <button
                        onClick={toggle}
                        className="flex items-center justify-center p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                        <AlignJustify className="w-6 h-6" />
                    </button>

                    <h1 className="flex-1 text-center px-4 text-lg font-semibold text-gray-900">
                        {program?.name || "Rewards Portal"}
                    </h1>

                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                                <span className="text-white font-semibold text-sm">{getUserInitials()}</span>
                            </div>
                            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
                        </button>

                        {isDropdownOpen && (
                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                                <div className="px-4 py-3 border-b border-gray-100">
                                    <p className="text-sm font-medium text-gray-900 truncate">{session.email}</p>
                                </div>
                                <div className="py-1">
                                    <button
                                        onClick={handleSwitchProgram}
                                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                        <Building2 className="w-4 h-4 mr-3 text-gray-400" />
                                        Switch Program
                                    </button>
                                </div>
                                <div className="border-t border-gray-100 pt-1">
                                    <button
                                        onClick={handleLogout}
                                        className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                    >
                                        <LogOut className="w-4 h-4 mr-3" />
                                        Logout
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <div
                className={`fixed lg:hidden inset-0 bg-white/80 backdrop-blur-sm z-50 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                onClick={close}
            >
                <div
                    className={`absolute inset-y-0 w-full transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex flex-col h-full bg-white">
                        <div className="flex flex-col gap-2 justify-between p-4 border-b border-gray-100">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-400">
                                    Lionsfloor Rewards Portal
                                </h2>
                                <button
                                    onClick={close}
                                    className="p-2 text-gray-400 hover:text-gray-600"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>
                        <div className="min-h-0 flex-grow flex flex-col justify-between">
                            <nav className="flex-grow overflow-y-auto">
                                <ul className="p-4 space-y-1">
                                    {navItems.map((item: any) => (
                                        <MobileNavLink
                                            close={close}
                                            key={item.name}
                                            blank={item.blank}
                                            href={item.href ? item.href : ""}
                                            icon={item.icon}
                                            name={item.name}
                                        />
                                    ))}
                                </ul>
                            </nav>
                            <div className="border-t border-gray-100">
                                <LogoutLink />
                            </div>
                            <NavFooter />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MobileNavbar;
